import React from 'react';
import Button from './UI/Button';
import { LINK_TEXT } from '../utils/clickable';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';
import { cardClass } from './UI/dashboardCard';

// Props:
// - summary: GET /api/loans/my-summary. Its next_payment is the next
//   instalment the API worked out (not the loan's final due date): its date,
//   amount and how many days until it (negative when late).
// - loans: array of loan objects (to open the invoice of the loan it is for)
// - onViewInvoice: fn(loan)
// - onDownloadPdf: fn(loan)
// - onDownloadHtml: fn(loan)
// - onViewLoan: fn(loan) — the loan code (and the amounts, when there is no
//   invoice to open) open the loan's details
// - fallback: what to show instead when nothing is due
const NextPaymentCard = ({ summary = null, loans = [], onViewInvoice, onDownloadPdf, onDownloadHtml, onViewLoan, fallback = null }) => {
  const next = summary?.next_payment || null;

  const formatDate = (dateString) => {
    return formatDateDMY(dateString);
  };

  // Nothing to pay (e.g. the loan is still being processed): the fallback card.
  if (!next) return fallback;

  const loan = (loans || []).find(l => l.id === next.loan?.id) || next.loan || {};
  const daysUntil = Number(next.days_until) || 0;
  const isOverdue = daysUntil < 0;
  const canViewInvoice = loan.state?.actions?.view_invoice === true;
  // An amount opens the invoice where there is one, otherwise the loan.
  const openAmount = canViewInvoice && typeof onViewInvoice === 'function'
    ? () => onViewInvoice(loan)
    : (typeof onViewLoan === 'function' ? () => onViewLoan(loan) : null);
  const Linked = ({ onClick, title, children }) => (onClick
    ? <button type="button" onClick={onClick} title={title} className={`font-medium ${LINK_TEXT}`}>{children}</button>
    : <span className="font-medium">{children}</span>);
  const amountTitle = canViewInvoice ? 'Open the invoice' : 'Open the loan';

  return (
    <div className={`${cardClass('green')} p-4 pt-5`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Next Payment</h2>
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : daysUntil <= 7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
          {isOverdue ? `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'}` :
            daysUntil === 0 ? 'Due today' : `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`}
        </span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-700 dark:text-slate-300">
          <div className="flex flex-wrap gap-3">
            <div><span className="text-gray-500 dark:text-slate-400">Loan:</span> <Linked onClick={typeof onViewLoan === 'function' ? () => onViewLoan(loan) : null} title="Open the loan">{loan.loan_code || `#${loan.id}`}</Linked></div>
            <div><span className="text-gray-500 dark:text-slate-400">Amount Due:</span> <Linked onClick={openAmount} title={amountTitle}>{toCurrency((Number(next.amount) || 0))}</Linked></div>
            <div><span className="text-gray-500 dark:text-slate-400">Due Date:</span> <span className="font-medium">{formatDate(next.date)}</span></div>
            {next.of > 1 && (
              <div><span className="text-gray-500 dark:text-slate-400">Instalment:</span> <span className="font-medium">{next.instalment} of {next.of}</span></div>
            )}
            <div><span className="text-gray-500 dark:text-slate-400">Outstanding:</span> <Linked onClick={openAmount} title={amountTitle}>{
              loan.total_due != null && Number.isFinite(Number(loan.total_due)) ? toCurrency(Number(loan.total_due)) : '-'
            }</Linked></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {typeof onViewInvoice === 'function' && canViewInvoice && (
            <Button variant="primary" onClick={() => onViewInvoice(loan)}>View Invoice</Button>
          )}
          {typeof onDownloadPdf === 'function' && canViewInvoice && (
            <Button onClick={() => onDownloadPdf(loan)}>PDF</Button>
          )}
          {typeof onDownloadHtml === 'function' && canViewInvoice && (
            <Button onClick={() => onDownloadHtml(loan)}>HTML</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NextPaymentCard;
